import { Component, AfterViewInit, ViewChild, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';

// Importaciones de servicios.
import { EmployeeService } from '../../../services/employee.service';

// Importaciones de componentes.
import { EmployeeCreateDialogComponent } from '../../../components/employee-create-dialog/employee-create-dialog.component';
import { EmployeeDeleteDialogComponent } from '../../../components/employee-delete-dialog/employee-delete-dialog.component';
import { EmployeeUpdateDialogComponent } from '../../../components/employee-update-dialog/employee-update-dialog.component';

// Importaciones de interfaces.
import { IEmployee } from '../../../interfaces/employee.interface';
import { IEmployeeResponse } from '../../../interfaces/employee-response.interface';

// Importaciones de modelos.
import { Employee } from '../../../models/employee.model';
import { Role } from '../../../models/role.model';
import { EmployeeUpdatePasswordDialogComponent } from '../../../components/employee-update-password-dialog/employee-update-password-dialog.component';

/**
 * Componente de configuración de empleados.
 *
 * Este componente proporciona una interfaz para gestionar la configuración de los empleados dentro de la aplicación.
 * Permite realizar ajustes relacionados con los empleados, como actualizar información, cambiar roles o gestionar permisos.
 */
@Component({
  selector: 'app-employee-settings',
  templateUrl: './employee-settings.component.html',
  standalone: false,
})
export class EmployeeSettingsComponent implements OnInit, AfterViewInit {
  /**
   * Indica si los empleados están siendo cargados.
   *
   * Esta propiedad se utiliza para mostrar un indicador de carga mientras se recuperan los empleados
   * de la fuente de datos.
   */
  loading: boolean = false;

  /**
   * Columnas que se mostrarán en la tabla de empleados.
   *
   * Esta propiedad define las columnas que se mostrarán en la tabla de empleados, incluyendo los campos
   * relacionados con la información básica del empleado.
   */
  tableColumns: string[] = [
    'displayName',
    'username',
    'email',
    'role.displayName',
    'disabled',
    'updatedAt',
    'createdAt',
    'actions',
  ];

  /**
   * Datos de la tabla de empleados.
   *
   * Esta propiedad contiene los datos de los empleados que se mostrarán en la tabla, utilizando `MatTableDataSource`
   * para manejar y mostrar los datos de manera eficiente, incluyendo funcionalidades como paginación y filtrado.
   */
  tableData: MatTableDataSource<IEmployee> =
    new MatTableDataSource<IEmployee>();

  /**
   * Paginador de la tabla de empleados.
   *
   * Esta propiedad se utiliza para la paginación de los datos en la tabla de empleados. Se hace referencia a un
   * componente `MatPaginator` que permite navegar entre las diferentes páginas de la tabla.
   */
  @ViewChild(MatPaginator) tablePaginator!: MatPaginator;

  /**
   * Referencia al componente MatSort que se utiliza para habilitar la funcionalidad de ordenación en la tabla.
   *
   * Se utiliza el decorador `@ViewChild` para acceder a la instancia del componente MatSort asociado a la tabla de los empleados.
   * Esto permite aplicar la funcionalidad de ordenación en las columnas de la tabla, permitiendo a los usuarios ordenar los
   * datos de forma ascendente o descendente según la columna seleccionada.
   *
   * @see MatSort - Componente de Angular Material que proporciona capacidades de ordenación para las tablas.
   */
  @ViewChild(MatSort) tableSort!: MatSort;

  /**
   * Constructor del componente.
   *
   * Este constructor inyecta las dependencias necesarias para el funcionamiento del componente.
   *
   * @param employeeService - Servicio para gestionar la información de los empleados.
   * @param matDialog - Servicio para abrir cuadros de diálogo.
   * @param snackBar - Servicio para mostrar notificaciones de tipo snack bar.
   */
  constructor(
    private employeeService: EmployeeService,
    private matDialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Método del ciclo de vida de Angular que se ejecuta una vez que el componente
   * ha sido inicializado, pero antes de que se renderice la vista en el DOM.
   *
   * Este método se utiliza para inicializar datos o realizar cualquier operación
   * que deba llevarse a cabo antes de que el componente sea visible para el usuario.
   * En este caso, se llama a la función `initTable()` para inicializar y cargar
   * los datos necesarios para la tabla.
   */
  ngOnInit(): void {
    this.initTable();
  }

  /**
   * Método del ciclo de vida de Angular que se ejecuta después de que
   * la vista del componente y sus vistas hijas hayan sido completamente inicializadas.
   *
   * Este método se utiliza para realizar inicializaciones adicionales que
   * requieren acceso a las vistas del componente (como elementos DOM,
   * directivas, o componentes hijos).
   */
  ngAfterViewInit(): void {
    this.configureTable();
  }

  /**
   * Inicializa la tabla de empleados.
   *
   * Este método se encarga de configurar y cargar los datos necesarios para la tabla de empleados.
   * Puede incluir la obtención de datos desde un servicio, la configuración de la paginación
   * y otras configuraciones relacionadas con la visualización de la tabla.
   */
  private async initTable(): Promise<void> {
    this.loading = true;
    this.tableData = new MatTableDataSource<IEmployee>();

    try {
      // Obtener los empleados y añadir-los a la tabla.
      const employeeResponse: IEmployeeResponse =
        await this.employeeService.getAll();
      this.tableData = new MatTableDataSource<IEmployee>(
        employeeResponse.employees.map((employee: IEmployee) => {
          return new Employee({ ...employee, role: new Role(employee.role) });
        })
      );
    } finally {
      this.configureTable();
      this.loading = false;
    }
  }

  /**
   * Configura la tabla de datos con las opciones necesarias, como la ordenación y la paginación.
   *
   * Se asegura de que la tabla esté configurada correctamente para su visualización y que las
   * funcionalidades de paginación y ordenación estén habilitadas, si es necesario.
   */
  private configureTable(): void {
    this.tableData.data?.reverse();

    // Asignar el `paginator` y el `sort` si existen.
    this.tablePaginator && (this.tableData.paginator = this.tablePaginator);
    this.tableSort && (this.tableData.sort = this.tableSort);
  }

  /**
   * Recarga la tabla de empleados.
   *
   * Esta función se encarga de actualizar la vista de la tabla de empleados
   * para reflejar los cambios recientes en los datos. Puede ser útil después
   * de realizar acciones como agregar, editar o eliminar empleados, asegurando
   * que la interfaz siempre muestre la información más actualizada.
   */
  reload(): void {
    this.initTable();
  }

  /**
   * Crea un nuevo empleado.
   *
   * Esta función abre un diálogo en la interfaz de usuario para permitir al
   * usuario ingresar los datos de un nuevo empleado. Una vez que el usuario
   * ha completado el formulario y confirmado la acción, la función recoge
   * la información ingresada y crea un nuevo registro de empleado. Si la
   * creación es exitosa, se actualiza la lista de empleados.
   */
  create(): void {
    this.matDialog
      .open(EmployeeCreateDialogComponent, {
        width: '500px',
        maxWidth: '100%',
      })
      .afterClosed()
      .subscribe((isCreated: boolean) => {
        if (isCreated) {
          this.initTable();
        }
      });
  }

  /**
   * Actualiza la información de un empleado.
   *
   * Este método se encarga de actualizar los datos de un empleado específico, generalmente a través
   * de una llamada a un servicio para realizar la actualización en la base de datos o en el servidor.
   * El parámetro `employee` contiene los nuevos valores que se deben guardar.
   *
   * @param employee - El objeto que contiene la información del empleado.
   */
  update(employee: IEmployee) {
    this.matDialog
      .open(EmployeeUpdateDialogComponent, {
        width: '500px',
        maxWidth: '100%',
        data: employee,
      })
      .afterClosed()
      .subscribe((isUpdated: boolean) => {
        if (isUpdated) {
          this.initTable();
        }
      });
  }

  /**
   * Abre un cuadro de diálogo para actualizar la contraseña de un empleado.
   *
   * Este método muestra un cuadro de diálogo modal que permite al usuario cambiar
   * la contraseña de un empleado específico. El cuadro de diálogo recibe los datos
   * del empleado como entrada.
   *
   * @param employee - El objeto que contiene la información del empleado.
   */
  updatePassword(employee: IEmployee) {
    this.matDialog.open(EmployeeUpdatePasswordDialogComponent, {
      width: '500px',
      maxWidth: '100%',
      data: employee,
    });
  }

  /**
   * Elimina un empleado de la lista.
   *
   * Abre un diálogo de confirmación para eliminar un empleado de la lista.
   * Si el usuario confirma la acción, el empleado es eliminado.
   *
   * @param employee - El objeto empleado que se va a eliminar.
   */
  delete(employee: IEmployee): void {
    this.matDialog
      .open(EmployeeDeleteDialogComponent, {
        width: '500px',
        maxWidth: '100%',
        data: employee,
      })
      .afterClosed()
      .subscribe((isDeleted: boolean) => {
        if (isDeleted) {
          this.initTable();
        }
      });
  }

  /**
   * Actualiza el estado de `employee.disabled` y lo guarda en la base de datos.
   * Revierte el cambio si ocurre un error.
   *
   * @param employee - Objeto a actualizar.
   * @param isChecked - Nuevo estado del toggle.
   */
  async toggleStatus(employee: IEmployee, isChecked: boolean): Promise<void> {
    const previousValue = employee.disabled;
    employee.disabled = Number(!isChecked);

    try {
      const employeeResponse: string = await this.employeeService.update(
        employee
      );
      this.snackBar.open(employeeResponse, '', {
        duration: 5000,
      });
    } catch (errors: any) {
      employee.disabled = previousValue;
    }
  }
}
